import { HTMLProps, useEffect } from 'react';

import { useLocation } from 'react-router';

import {
  IconChartBar,
  IconNotes,
  IconReportSearch,
  IconSettings,
  IconTool,
} from '@tabler/icons-react';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/hooks/useAuth';

import { cn } from '@/utils/tailwind';

import Constants from 'constants/index';

import NavbarDesktop from './NavbarDesktop';
import NavbarMobile from './NavbarMobile';

import { clearDocumentAtom, setDocumentAtom } from '@/atoms/document-persisted';

const Navbar = ({ className }: HTMLProps<HTMLDivElement>) => {
  const { t } = useTranslation();
  const location = useLocation();
  const setDocument = useSetAtom(setDocumentAtom);
  const clearDocument = useSetAtom(clearDocumentAtom);
  const { data } = useAuth();
  const features = data?.features;

  useEffect(() => {
    setDocument({ routedFrom: 'link', document: null });
    return () => {
      const docPath = `/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}/`;
      if (location.pathname.startsWith(docPath)) {
        clearDocument();
      }
    };
  }, [location.pathname, setDocument, clearDocument]);

  const isExploreTab =
    location.pathname === '/' ||
    location.pathname.startsWith(
      `/${Constants.ROUTE_PATH_NAMES.CATEGORY_PATH_NAME}`,
    ) ||
    // This assumes doc is only used in explore.
    location.pathname.startsWith(
      `/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}`,
    ) ||
    // This assumes search is only used in explore.
    location.pathname.startsWith(
      `/${Constants.ROUTE_PATH_NAMES.SEARCH_PATH_NAME}`,
    );

  const isPlansTab = location.pathname.startsWith(
    `/${Constants.ROUTE_PATH_NAMES.PLANS_PATH_NAME}`,
  );

  const isSettingsTab = location.pathname.startsWith(
    `/${Constants.ROUTE_PATH_NAMES.SETTINGS_PATH_NAME}`,
  );

  const isAdminTab = location.pathname.startsWith(
    `/${Constants.ROUTE_PATH_NAMES.ADMIN_PATH_NAME}`,
  );

  const isReportsTab = location.pathname.startsWith(
    `/${Constants.ROUTE_PATH_NAMES.REPORTS_PATH_NAME}`,
  );

  const tabs = [
    {
      label: t('TrainingPlans.tabExplore'),
      icon: <IconReportSearch />,
      to: '/',
      isActive: isExploreTab,
      showTab: true,
    },
    {
      label: t('TrainingPlans.tabPlans'),
      icon: <IconNotes />,
      to: `/plans/my-plans`,
      isActive: isPlansTab,
      showTab: features?.plans?.enabled,
    },
    {
      label: t('Generic.reports'),
      icon: <IconChartBar />,
      to: `/reports/plans`,
      isActive: isReportsTab,
      showTab: features?.reporting?.enabled,
    },
    {
      label: t('Generic.settings'),
      icon: <IconSettings />,
      to: `/settings`,
      isActive: isSettingsTab,
      showTab: features?.settings?.enabled,
    },
    {
      label: t('Generic.admin'),
      icon: <IconTool />,
      to: `/admin`,
      isActive: isAdminTab,
      showTab: features?.admin?.enabled,
    },
  ];

  const filteredTabs = tabs.filter(tab => tab.showTab);

  // Suspends rendering until we have actual user data
  return features && Object.keys(features).length ? (
    <>
      <NavbarDesktop
        className={cn('hidden md:block', className)}
        tabs={filteredTabs}
      />
      {window.native.showNavbar && (
        <NavbarMobile
          className={cn('md:hidden', className)}
          tabs={filteredTabs}
        />
      )}
    </>
  ) : null;
};

export default Navbar;
